import React, {ChangeEvent, FC, useEffect, useState} from 'react';
import {
  useDebounce,
  useLocalStorage,
  useSortColumn,
  useUpdateEffect,
} from 'hooks';
import {Button, Pagination, PaginationItem} from '@mui/material';
import {
  CreateIcon,
  PaginationArrowLeft,
  PaginationArrowRight,
} from 'assets/icons';
import {useGetClientsQuery} from 'store/slices/clients';
import {defaultStatus, StatusOption} from 'core/constants';
import ClientsTable from './ClientsTable/ClientsTable';
import ClientsCreate from './ClientsCreate/ClientsCreate';
import ClientsTopToolbar from './ClientsTopToolbar/ClientsTopToolbar';
import {createButton} from 'styles/MUIStyles/buttons';
import {pagination} from 'styles/MUIStyles/common';
import styles from './Clients.module.scss';

const Clients: FC = () => {
  const [pageSize, setPageSize] = useLocalStorage('clientsPageSize', 10);
  const [pageNumber, setPageNumber] = useLocalStorage('clientsPageNumber', 1);
  const [search, setSearch] = useLocalStorage('clientsSearch', '');
  const debouncedSearch = useDebounce(search, 800);
  const [isCreateClientsModalOpen, setIsCreateClientsModalOpen] =
    useState(false);
  const [status, setStatus] = useLocalStorage<StatusOption | null>(
    'clientsStatus',
    defaultStatus,
  );
  // null остаётся в localStorage у тех, кто когда-то очищал фильтр, - для них
  // тоже открываем «Active», а не пустой селект
  const activeStatus = status ?? defaultStatus;
  const [sortConfig, handleColumnClick] = useSortColumn('clientsSortConfig');
  const [
    isRequestWithDeletedStatusSuccess,
    setIsRequestWithDeletedStatusSuccess,
  ] = useState(false);

  const {
    data: clientsData,
    originalArgs,
    isSuccess,
  } = useGetClientsQuery({
    pageSize,
    pageNumber,
    search: debouncedSearch,
    status: activeStatus,
    sortBy: sortConfig,
  });

  const handleOnPageChange = (event: ChangeEvent<unknown>, page: number) => {
    setPageNumber(page);
  };

  useEffect(() => {
    if (
      originalArgs &&
      originalArgs?.status?.queryName === 'isDeleted' &&
      isSuccess
    ) {
      setIsRequestWithDeletedStatusSuccess(true);
    } else {
      setIsRequestWithDeletedStatusSuccess(false);
    }
  }, [originalArgs, isSuccess]);

  useUpdateEffect(() => {
    if (pageNumber > 1) {
      setPageNumber(1);
    }
  }, [debouncedSearch, activeStatus, pageSize]);

  return (
    <div style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
      <div className={styles.clientsTop}>
        <h1 className={styles.clientsTitle}>Users</h1>
        <Button
          variant="contained"
          startIcon={<CreateIcon />}
          onClick={() => setIsCreateClientsModalOpen(true)}
          sx={createButton}
        >
          Create
        </Button>
      </div>
      <div className={styles.clientsMainContent}>
        <div className={styles.tableFiltersTop}>
          <ClientsTopToolbar
            search={search}
            setSearch={setSearch}
            status={activeStatus}
            setStatus={setStatus}
            pageSize={pageSize}
            setPageSize={setPageSize}
            metaData={clientsData?.metadata || null}
          />
        </div>
        <div style={{height: '87%', flexGrow: 1}}>
          <ClientsTable
            clients={clientsData?.data || []}
            metaData={clientsData?.metadata || null}
            search={debouncedSearch}
            sortConfig={sortConfig}
            handleColumnClick={handleColumnClick}
            isRequestWithDeletedStatusSuccess={
              isRequestWithDeletedStatusSuccess
            }
          />
        </div>
      </div>
      <div className={styles.pagination}>
        <Pagination
          count={clientsData?.metadata?.pageCount || 1}
          page={pageNumber}
          onChange={handleOnPageChange}
          sx={pagination}
          renderItem={(item) => (
            <PaginationItem
              slots={{
                previous: PaginationArrowLeft,
                next: PaginationArrowRight,
              }}
              {...item}
            />
          )}
        />
      </div>

      <ClientsCreate
        isCreateClientsModalOpen={isCreateClientsModalOpen}
        setIsCreateClientsModalOpen={setIsCreateClientsModalOpen}
      />
    </div>
  );
};

export default Clients;
